import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import { useHistory } from 'react-router-dom';
import { DASHBOARD_ASSISTANT_RUNTIME_MODE } from '../constants';
import { hasPermission, restrictedResult } from '../lib/permissions';
import { validateDashboardRecordId } from '../lib/recordValidation';
import { stringToolParameters } from '../lib/assistantToolSchemas';
import {
  createFrontendNavigationToolMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';

export function useRecordOpenAction({
  description,
  dispatchActionMessage,
  name,
  parameterName,
  parameterDescription,
  getPath,
  permission,
  validateRecordType,
}) {
  const history = useHistory();

  const parameters = useMemo(
    () =>
      stringToolParameters({
        name: parameterName,
        description: parameterDescription,
      }),
    [parameterDescription, parameterName],
  );

  const tool = useMemo(
    () => ({
      toolName: name,
      type: 'frontend',
      description,
      parameters,
      execute: async (args) => {
        const id = args?.[parameterName]?.trim?.();
        if (!id) {
          const result = { status: 'error', message: `${parameterName} is required.` };
          dispatchActionMessage?.(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: args,
            toolName: name,
            toolResult: result,
          }));
          return result;
        }

        if (!hasPermission(permission)) {
          const result = restrictedResult(name);
          dispatchActionMessage?.(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: args,
            toolName: name,
            toolResult: result,
          }));
          return result;
        }

        if (validateRecordType) {
          const validation = await validateDashboardRecordId(validateRecordType, id);
          if (!validation.isValid) {
            const result = {
              runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
              status: 'error',
              message:
                validation.message ||
                `${id} is not a valid CharityStack ${validateRecordType} ID. Search for the record first and use the exact ID returned by search_dashboard_records.`,
            };
            dispatchActionMessage?.(createFrontendToolErrorMessage({
              message: result.message,
              status: result.status,
              toolArgs: args,
              toolName: name,
              toolResult: result,
            }));
            return result;
          }
        }

        const path = getPath(encodeURIComponent(id));
        history.push(path);
        const result = { runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE, status: 'opened', path };
        dispatchActionMessage?.(createFrontendNavigationToolMessage({
          label: validateRecordType || id,
          path,
          toolArgs: args,
          toolName: name,
          toolResult: result,
        }));
        return result;
      },
    }),
    [
      description,
      dispatchActionMessage,
      getPath,
      history,
      name,
      parameterName,
      parameters,
      permission,
      validateRecordType,
    ],
  );

  useAssistantTool(tool);
}
